import { faBadgeCheck, faCamera, faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Badge, Box, Stack, Typography, TypographyProps } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import { getTeamMemberDisplayName } from '../utils/nameUtils';

export type AvatarSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface AvatarIndicators {
    isLicensedCaregiver?: boolean;
    isAdmin?: boolean;
    showPhotoUploadBadge?: boolean;
}

export const AvatarSizeMap: Record<AvatarSize, number> = {
    small: 24,
    medium: 48,
    large: 80,
    xlarge: 120,
};

const FontVariantMap: Record<AvatarSize, TypographyProps['variant']> = {
    small: 'subtitle1',
    medium: 'body1',
    large: 'h2',
    xlarge: 'h1',
};

const StyledLicensedCareGiverBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.shadows[1],
        padding: theme.spacing(0.5),
        borderRadius: '50%',
        width: theme.spacing(3),
        height: theme.spacing(3),
    },
}));

const StyledPhotoUploadBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.shadows[1],
        padding: theme.spacing(0.5),
        borderRadius: theme.spacing(1.5),
        width: theme.spacing(5),
        height: theme.spacing(5),
    },
}));

const StyledAdminBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.shadows[1],
        padding: theme.spacing(0.5),
        borderRadius: theme.spacing(1.5),
        width: '100%',
        left: '50%',
    },
}));

export interface TeamMemberAvatarProps extends AvatarIndicators {
    size?: AvatarSize;
    teamMember: { firstName: string; lastName: string; preferredName?: string };
    onClick?: () => void;
}

function TeamMemberAvatar(props: TeamMemberAvatarProps) {
    const {
        size = 'medium',
        teamMember,
        isLicensedCaregiver = false,
        isAdmin = false,
        showPhotoUploadBadge = false,
        onClick,
    } = props;
    const avatarSize = AvatarSizeMap[size];
    const { palette } = useTheme();

    const teamMemberInitials =
        `${teamMember.firstName[0]}${teamMember.lastName[0]}`.toLocaleUpperCase();

    const baseAvatar = (
        <Avatar
            sx={{ width: avatarSize, height: avatarSize, backgroundColor: palette.purple?.[300] }}
            alt={getTeamMemberDisplayName(teamMember)}
        >
            <Typography
                variant={FontVariantMap[size]}
                sx={{
                    fontWeight: 600,
                    color: 'common.white',
                    transform: size === 'small' ? 'scale(0.6)' : 'none',
                }}
            >
                {teamMemberInitials}
            </Typography>
        </Avatar>
    );

    const avatarContent =
        isAdmin && size === 'large' ? (
            <StyledAdminBadge
                badgeContent={
                    <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                        <FontAwesomeIcon
                            icon={faLock}
                            color={palette.primary.main}
                            size="sm"
                            fixedWidth
                        />
                        <Typography variant="subtitle2">Admin</Typography>
                    </Stack>
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                variant="standard"
                overlap="circular"
            >
                {baseAvatar}
            </StyledAdminBadge>
        ) : isLicensedCaregiver && size === 'large' ? (
            <StyledLicensedCareGiverBadge
                badgeContent={
                    <FontAwesomeIcon
                        icon={faBadgeCheck}
                        color={palette.purple?.[400]}
                        size="xl"
                        fixedWidth
                    />
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="standard"
                overlap="circular"
            >
                {baseAvatar}
            </StyledLicensedCareGiverBadge>
        ) : showPhotoUploadBadge && (size === 'large' || size === 'xlarge') ? (
            <StyledPhotoUploadBadge
                badgeContent={
                    <FontAwesomeIcon
                        icon={faCamera}
                        color={palette.primary.main}
                        size="xl"
                        fixedWidth
                    />
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="standard"
                overlap="circular"
            >
                {baseAvatar}
            </StyledPhotoUploadBadge>
        ) : (
            baseAvatar
        );

    if (onClick) {
        return (
            <Box onClick={onClick} sx={{ cursor: 'pointer' }}>
                {avatarContent}
            </Box>
        );
    }

    return avatarContent;
}

export default TeamMemberAvatar;
